import { BehaviorSubject, Observable, ReplaySubject, Subscription, firstValueFrom, of, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { Navigation } from './navigation.types';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { compactNavigation, defaultNavigation, futuristicNavigation, horizontalNavigation } from './navigation.data';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { IMenu } from '@shared/models/menu';

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    navegacionMenu = [];
    inProcess = false;
    private _navigation: BehaviorSubject<Navigation> = new BehaviorSubject<Navigation>({
        compact: [],
        default: [],
        futuristic: [],
        horizontal: []
    });
    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
        // Inicializar la navegación con los menús predeterminados
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter para la navegación
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter para los datos del menú de navegación
     */
    set _navigationMenuData(menuData) {
        this.navegacionMenu = this.parsearMenuNavegacion(menuData);
        // Actualizar la navegación con los nuevos datos del menú
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    /**
     * Getter para los datos del menú de navegación
     */
    get _navigationMenuData() {
        return this.navegacionMenu;
    }

    /**
     * Esta función se utiliza para parsear los datos del menú de navegación
     * que devuelve el servicio backend.
     * @param menu 
     * @returns 
     */
    parsearMenuNavegacion(menu: IMenu[]) {
        let menuData = [];
        
        menu.forEach(i => {
            // Verificar si el elemento tiene un orden diferente de -1
            if (i.item.orden !== -1) {
                let { hijos, hiddenChildren } = this.tieneHijos(i.hijos);
        
                let item = {
                    id: i.item.descripcion.toLowerCase().replace(' ', ''),
                    title: i.item.descripcion,
                    type: i.item.ruta ? 'basic' : (hijos.length > 0 ? 'collapsable' : 'basic'),
                    link: i.item.ruta ? i.item.ruta : null,
                    icon: i.item.icono ? i.item.icono : null,
                    children: hijos,
                    hiddenChildren: hiddenChildren // Agregar hiddenChildren aquí
                };
                menuData.push(item);
            }
        });
        return menuData;
    }
    
    /**
     * Función recursiva que recorre los submenús y devuelve los hijos visibles y ocultos.
     * @param hijos 
     */
    tieneHijos(hijos) {
        let data = [];
        let hiddenData = [];
        
        hijos.forEach(subItem => {
            // Verificar si el subItem tiene un orden diferente de -1
            if (subItem.item.orden !== -1) {
                let { hijos, hiddenChildren } = this.tieneHijos(subItem.hijos);
        
                let item = {
                    id: subItem.item.descripcion.toLowerCase().replace(' ', ''),
                    title: subItem.item.descripcion,
                    type: subItem.hijos.length > 0 ? 'collapsable' : 'basic',
                    link: subItem.item.ruta ? subItem.item.ruta : null,
                    icon: subItem.item.icono ? subItem.item.icono : null,
                    children: hijos
                };
    
                data.push(item);
            } else {
                // Si el subItem tiene un orden de -1, lo agregamos a hiddenData
                let hiddenItem = {
                    id: subItem.item.descripcion.toLowerCase().replace(' ', ''),
                    title: subItem.item.descripcion,
                    type: subItem.hijos.length > 0 ? 'collapsable' : 'basic',
                    link: subItem.item.ruta ? subItem.item.ruta : null,
                    icon: subItem.item.icono ? subItem.item.icono : null,
                    children: this.tieneHijos(subItem.hijos).hijos // Obtener hijos visibles
                };
                hiddenData.push(hiddenItem);
            }
        });
    
        return { hijos: data, hiddenChildren: hiddenData };
    }

    /**
     * Obtener los menús
     */
    getMenus() {
        if (!this.inProcess) {
            this.getMenusX().then(res => {
                this.inProcess = res;
            });
        }
    }

    async getMenusX(): Promise<boolean> {
        this.inProcess = true;

        const res = await firstValueFrom(this._httpClient.post(`${environment.menu}`, null));
        this._navigationMenuData = res['result'];

        return true;
    }

    /**
     * Obtener todos los datos de navegación
     */
    get(): Observable<Navigation> {
        const navigationData = {
            compact: this._compactNavigation,
            default: this._navigationMenuData,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        }

        return of(navigationData);
    }

    hasAccess(route: string): boolean {
        const menuItems = this.navegacionMenu;
        
        const checkAccess = (items): boolean => {
            for (const item of items) {
                // Verificar acceso en el elemento actual
                if (item.link === route || (item.link && route.startsWith(item.link))) {
                    return true; 
                }
                // Verificar acceso en los hijos visibles
                if (item.children && item.children.length > 0) {
                    const hasChildAccess = checkAccess(item.children);
                    if (hasChildAccess) {
                        return true; 
                    }
                }
                // Verificar acceso en los hijos ocultos
                if (item.hiddenChildren && item.hiddenChildren.length > 0) {
                    const hasHiddenChildAccess = checkAccess(item.hiddenChildren);
                    if (hasHiddenChildAccess) {
                        return true; 
                    }
                }
            }
            return false; 
        };
    
        const accessGranted = checkAccess(menuItems);
        return accessGranted;
    }
}
